import { useState, useEffect } from 'react';
import { useCart } from '../CartContext';
import { enqueueSnackbar } from 'notistack';

export const useCartActions = (product, initialVariant, cart, setCart) => {
  const { updateQuantity, getQuantityForVariant, itemQuantities } = useCart();
  const [selectedVariant, setSelectedVariant] = useState(initialVariant);
  const [showCartButton, setShowCartButton] = useState(true);

  // Get quantity for the currently selected variant
  const quantity = product && selectedVariant
    ? getQuantityForVariant(product._id, selectedVariant._id)
    : 0;

  // Update showCartButton based on quantity
  useEffect(() => {
    setShowCartButton(quantity === 0);
  }, [quantity]);

  // Add product to cart array if not already present
  const ensureProductInCart = () => {
    if (!cart.some(item => item._id === product._id)) {
      setCart([...cart, product]);
      localStorage.setItem("cartValue", JSON.stringify([...cart, product]));
    }
  };

  // Remove product from cart array
  const removeProductFromCart = () => {
    const updatedCart = cart.filter(item => item._id !== product._id);
    setCart(updatedCart);
    localStorage.setItem("cartValue", JSON.stringify(updatedCart));
  };

  // Check if product has any other variants in cart
  const hasOtherVariantsInCart = (excludeVariantId) => {
    return Object.keys(itemQuantities).some(key => {
      const item = itemQuantities[key];
      return (
        item.productId === product._id &&
        item.variant._id !== excludeVariantId &&
        item.quantity > 0
      );
    });
  };

  // Add selected variant to cart with quantity 1
  const handleAddToCart = (e) => {
    if (e) e.stopPropagation();
    if (!selectedVariant) return;

    // Stock check
    if (selectedVariant.availableStock === 0) {
      enqueueSnackbar('This item is out of stock', { variant: 'warning' });
      return;
    }

    ensureProductInCart();
    updateQuantity(
      product._id,
      1,
      selectedVariant.price,
      selectedVariant.offerPrice,
      selectedVariant
    );
    setShowCartButton(false);
    enqueueSnackbar('Item added to cart', { variant: 'success' });
  };

  // Increase quantity of selected variant
  const handleIncreaseQuantity = (e) => {
    if (e) e.stopPropagation();
    if (!selectedVariant) return;

    // Prevent exceeding available stock
    if (quantity >= selectedVariant.availableStock) {
      enqueueSnackbar(`Only ${selectedVariant.availableStock} available in stock`, { 
        variant: 'warning' 
      });
      return;
    }

    const newQuantity = quantity + 1;
    ensureProductInCart();
    updateQuantity(
      product._id,
      newQuantity,
      selectedVariant.price,
      selectedVariant.offerPrice,
      selectedVariant
    );
    setShowCartButton(false);
  };

  // Decrease quantity of selected variant
  const handleDecreaseQuantity = (e) => {
    if (e) e.stopPropagation();
    if (!selectedVariant || quantity === 0) return;

    const newQuantity = quantity - 1;
    updateQuantity(
      product._id,
      newQuantity,
      selectedVariant.price,
      selectedVariant.offerPrice,
      selectedVariant
    );

    // If quantity becomes 0 and no other variants exist, remove from cart
    if (newQuantity === 0 && !hasOtherVariantsInCart(selectedVariant._id)) {
      removeProductFromCart();
      setShowCartButton(true);
    }
  };

  // Change the selected variant
  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
  };

  return {
    selectedVariant,
    quantity,
    showCartButton,
    handleAddToCart,
    handleIncreaseQuantity,
    handleDecreaseQuantity,
    handleVariantSelect,
    setSelectedVariant,
  };
};